import React, { useEffect, useState } from 'react'
import { useLocation, useHistory } from 'react-router'
import { createContext } from 'utils/context'
import { useSelector } from 'store/redux.store'
import { debounce } from 'lodash'
import { useExtensions } from 'hooks/use-extensions'
import { sendEventToGa, sendToAstroboxWhenUserClickAtIconBN } from './menu/metrics'
import { ImenuItem } from './menu/types'
import { SidemenuContext } from './sidemenu.interfaces'
import { AUDIENCE_MENU_ID, AUDIENCE_SIDEMENU_SOURCE } from './sidemenu.constants'

const [useSidemenu, SidemenuCtx] = createContext<SidemenuContext>()

const maxMobileQuery = 600
const help_central = 'knowledge_central'
const Provider = ({ children }) => {
  const history = useHistory()
  const { context, user } = useSelector(state => state)
  const { pathname: active } = useLocation()
  const [isFixedOpen, setIsFixedOpen] = useState<SidemenuContext['isMenuOpen']>(false)
  const [isModalOpen, setIsModalOpen] = useState<SidemenuContext['isModalOpen']>(false)
  const [isHoveredOpen, setIsHoveredOpen] = useState<SidemenuContext['isMenuOpen']>(false)
  const [submenu, setSubmenu] = useState<SidemenuContext['submenu']>(null)
  const [isSubMenuSidebarActive, setIsSubMenuSidebarActive] = useState<boolean>(false)
  const [activeSubmenuItems, setActiveSubmenuItems] = useState<{}>({})
  const { setActivationSource } = useExtensions()

  function toggleSubmenu(value) {
    value === submenu ? setSubmenu(null) : setSubmenu(value)
  }

  useEffect(() => {
    !isFixedOpen && setIsSubMenuSidebarActive(false)
  }, [isFixedOpen])

  const menuStates = {
    isHoveredOpen,
    isFixedOpen
  }

  const handleMouseEnter = () => {
    if (!isHoveredOpen && window.innerWidth > maxMobileQuery) {
      setIsHoveredOpen(true)
    }
  }

  const handleMouseLeave = debounce(() => {
    if (isHoveredOpen) {
      setIsHoveredOpen(false)
    }
  }, 500)

  const handleKeyDown = debounce(event => {
    if (event.key === 'Escape') {
      setIsHoveredOpen(false)
    }
  }, 500)

  const handleUserNavigation = {
    onMouseEnter: handleMouseEnter,
    onFocus: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onBlur: handleMouseLeave,
    onKeyDown: handleKeyDown
  }

  const isMenuOpen = isHoveredOpen || isFixedOpen

  const toggleMenu = debounce(() => {
    setIsFixedOpen(oldValue => !oldValue)
  }, 50)

  const closeMenu = debounce(() => {
    isFixedOpen && setIsFixedOpen(false)
  }, 50)

  const isOnMobileExperienceCloseMenu = (isMenuOpen, maxMobileQuery, toggleMenu) => {
    if (window.innerWidth <= maxMobileQuery && isMenuOpen) {
      toggleMenu()
    }
  }

  const onClickItem = (menuItem: ImenuItem) => {
    const hasSubmenu = menuItem.submenu.length > 0

    if (menuItem.id === AUDIENCE_MENU_ID) {
      setActivationSource(AUDIENCE_SIDEMENU_SOURCE)
    }

    if (menuItem.id === help_central) {
      setIsModalOpen(true)
    }
    if (!hasSubmenu) {
      isOnMobileExperienceCloseMenu(isMenuOpen, maxMobileQuery, toggleMenu)
    }

    if (menuItem.route?.path && !menuItem.route?.isExternalRoute) {
      history.push(menuItem.route.path)
    }
    if (menuItem.route?.path && menuItem.route?.isExternalRoute) {
      window.open(menuItem.route.path, '_blank', 'noopener')
    }

    if (hasSubmenu) {
      toggleSubmenu(menuItem.id)
    }
    sendToAstroboxWhenUserClickAtIconBN(menuItem, {
      userId: user.id,
      userType: context.userTypeInfo.userType,
      userSegmentation: context.userTypeInfo.userSegmentationInfo
    })
    sendEventToGa(menuItem.eventLayer)
  }

  const onMouseEnter = (menuItem: ImenuItem) => {
    const hasSubmenu = Boolean(menuItem.submenu.length)
    const currentMenuSubItems = context.sideMenu.find(item => item.id === menuItem.id)

    if (hasSubmenu) {
      toggleSubmenu(menuItem.id)
      if (currentMenuSubItems?.submenu) {
        setActiveSubmenuItems({
          submenu: currentMenuSubItems?.submenu,
          selectedItem: menuItem
        })
        setIsSubMenuSidebarActive(true)
      }
    }

    if (currentMenuSubItems?.position && !hasSubmenu) {
      setIsSubMenuSidebarActive(false)
    }
  }

  const onMouseLeaveSubmenuSidebar = () => setIsSubMenuSidebarActive(false)

  return (
    <SidemenuCtx
      value={{
        active,
        submenu,
        menuStates,
        handleUserNavigation,
        isMenuOpen,
        toggleMenu,
        closeMenu,
        maxMobileQuery,
        onClickItem,
        onMouseEnter,
        setIsHoveredOpen,
        isHoveredOpen,
        isSubMenuSidebarActive,
        onMouseLeaveSubmenuSidebar,
        setIsSubMenuSidebarActive,
        activeSubmenuItems,
        isModalOpen,
        setIsModalOpen
      }}
    >
      {children}
    </SidemenuCtx>
  )
}

export { useSidemenu, Provider }
